import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Tab, TabList, Tabs } from "@similarweb/ui-components";

import { MAX_DOMAINS_IN_CATEGORY } from "app/components/customCategoriesWizard/custom-categories-max-domains";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import { useLoading } from "app/custom-hooks/loadingHook";
import type { IIndustryState } from "app/pages/industry-analysis/market-builder/tabs/types";
import { INITIAL_INDUSTRY_TAB_STATE } from "app/pages/industry-analysis/market-builder/tabs/types";
import { openDefaultHooksModal } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";

import { useBuilderContext } from "../builderContext";
import type { IDomain, IDomains } from "../MarketBuilder.types";
import { ESelectDomainOptions } from "../MarketBuilder.types";
import MarketBuilderService from "../services/ApiService";
import { getTabFromIndex, tabList } from "../tabsConfig";

import Industry from "./industry/Industry";
import { BuilderTabsWrapper, LockIcon, TabIcon, TabPanel, TabPanelWrapper } from "./StyledComponents";
import Websites from "./websites/Websites";

const AMOUNT_OF_SUGGESTIONS = 20;

interface IBuilderTabs {
    selectedEntities: IDomains;
    selectEntities: (domainsToAdd: IDomains, shouldSyncFavicons?: boolean) => void;
    serializedSuggestedData: IDomains;
    updateSuggestedEntities: (domains: IDomains) => void;
    onBuildSegmentClick: VoidFunction;
    isSegmentsLoading: boolean;
}

const BuilderTabs: FC<IBuilderTabs> = ({
    selectedEntities,
    selectEntities,
    serializedSuggestedData,
    updateSuggestedEntities,
    onBuildSegmentClick,
    isSegmentsLoading,
}) => {
    const { selectedTabIndex, setSelectedTabIndex, selectedCountry } = useBuilderContext();
    const [industryTabState, setIndustryTabState] = useState<IIndustryState>(INITIAL_INDUSTRY_TAB_STATE);
    const marketBuilderService = useMemo(() => MarketBuilderService.getInstance(), []);
    const [suggestedData, suggestedDataOps] = useLoading();
    const maxAllowedDomains = MAX_DOMAINS_IN_CATEGORY - selectedEntities.length;
    const [hasFetchedInitialSuggestions, setHasFetchedInitialSuggestions] = useState(false);

    // on edit mode first load we want to call the getSuggestions once (wait for segments to load if applicable):
    useEffect(() => {
        if (hasFetchedInitialSuggestions || isSegmentsLoading) {
            return;
        }

        if (selectedEntities.length > 0) {
            getSuggestions(selectedEntities);
            setHasFetchedInitialSuggestions(true);
        }
    }, [selectedEntities, isSegmentsLoading, hasFetchedInitialSuggestions]);

    const getSuggestions = useCallback(
        (domains?) => {
            const getDomainName = (domainsArr: IDomains): string[] => {
                return domainsArr.map((entity: IDomain) => {
                    return entity.domain;
                });
            };
            const keys = domains ? getDomainName(domains) : getDomainName(selectedEntities);

            const apiParams = {
                keys: keys,
                country: selectedCountry?.id,
                results: AMOUNT_OF_SUGGESTIONS,
            };
            suggestedDataOps.load(() => {
                return marketBuilderService.getSuggestedDomains({
                    ...apiParams,
                });
            });
        },
        [marketBuilderService, selectedCountry, selectedEntities, suggestedDataOps],
    );

    const isSuggestionsLoading = [useLoading.STATES.LOADING].includes(suggestedData.state);

    useEffect(() => {
        if ([useLoading.STATES.INIT, useLoading.STATES.LOADING].includes(suggestedData.state) || !suggestedData.data) {
            return;
        }

        const newData = suggestedData?.data?.Data.map((domain): IDomain => {
            return {
                domain: domain.Site,
                favicon: domain.Favicon,
                id: domain.Site,
                type: EAssetType.Website,
                section: ESelectDomainOptions.WEBSITE,
            };
        });

        updateSuggestedEntities(newData);
    }, [isSuggestionsLoading]);

    // adds multiple domains to my-list
    const handleAddDomains = useCallback(
        (entities: IDomains, shouldSyncFavicons = false, section = ESelectDomainOptions.MANUAL) => {
            entities.forEach((entity) => {
                entity.section = section;
            });

            const updatedDomains = [...entities, ...selectedEntities];
            selectEntities(updatedDomains, shouldSyncFavicons);

            // remove new domains from suggestion list:
            const newSuggestedData = [];
            serializedSuggestedData.forEach((suggestedDomain: IDomain) => {
                let isDuplicated = false;
                entities.forEach((entity: IDomain) => {
                    if (suggestedDomain.domain === entity.domain) {
                        isDuplicated = true;
                    }
                });

                if (!isDuplicated) {
                    newSuggestedData.push(suggestedDomain);
                }
            });
            updateSuggestedEntities(newSuggestedData);
        },
        [selectEntities, selectedEntities, serializedSuggestedData, updateSuggestedEntities],
    );

    // add a single domain to my-list
    const handleAddDomain = useCallback(
        (entity: IDomain) => {
            const updatedDomains = [entity, ...selectedEntities];
            selectEntities(updatedDomains);

            const newSuggestedData = serializedSuggestedData.filter(
                (suggestedDomain) => suggestedDomain.domain !== entity.domain,
            );
            updateSuggestedEntities(newSuggestedData);
        },
        [selectEntities, selectedEntities, serializedSuggestedData],
    );

    const onTabSelected = (index) => {
        const tab = getTabFromIndex(index);
        allTrackers.trackEvent("Tab", "click", `Change tab/by ${tab.id}`);

        if (tab.isLocked) {
            openDefaultHooksModal("Industry", LocationService.getCurrentLocationTracking());
        } else {
            setSelectedTabIndex(index);
        }
    };

    return (
        <BuilderTabsWrapper data-automation="BuilderTabs">
            <Tabs
                selectedIndex={selectedTabIndex}
                onSelect={onTabSelected}
            >
                <TabList>
                    {Object.values(tabList).map((tab, index) => (
                        <Tab key={tab.id}>
                            <FlexRow data-automation={tab.dataAutomation}>
                                <TabIcon
                                    iconName={tab.icon}
                                    isSelected={selectedTabIndex === index}
                                />
                                {tab.title}
                                {tab.isLocked ? <LockIcon iconName="locked" /> : null}
                            </FlexRow>
                        </Tab>
                    ))}
                </TabList>
                <TabPanelWrapper>
                    <TabPanel>
                        <Websites
                            getSuggestedDomains={getSuggestions}
                            suggestedDomains={serializedSuggestedData}
                            isLoading={isSuggestionsLoading}
                            onAddWebsite={handleAddDomain}
                            selectedEntities={selectedEntities}
                            onAddMultipleWebsites={handleAddDomains}
                            maxAllowedDomains={maxAllowedDomains}
                            onBuildSegmentClick={onBuildSegmentClick}
                            isSegmentsLoading={isSegmentsLoading}
                        />
                    </TabPanel>
                    <TabPanel>
                        <Industry
                            selectedDomains={selectedEntities}
                            selectedCountry={selectedCountry}
                            addDomains={handleAddDomains}
                            industryTabState={industryTabState}
                            setIndustryTabState={setIndustryTabState}
                        />
                    </TabPanel>
                </TabPanelWrapper>
            </Tabs>
        </BuilderTabsWrapper>
    );
};

export default BuilderTabs;
